import { DocumentModel } from "../../features/draft/types/document";
import { Citation } from "../shared/citation";
import { RevealTarget } from "../shared/editInfo";
import {
  parseTableCellId,
  scrollToBookmark,
  scrollToParagraph,
  scrollToTableCell,
} from "./documentTools";

// Word's citation vocabulary: paragraph IDs (pA, pB, ... pAA) and table cell IDs
// (t0r1c2). The `<segment=ID>` tag convention is shared with every host; this is the
// half that knows what an ID means, what to label it, and how to get there.

/** Matches the segment IDs the Word system prompt tells the model to emit. */
export const WORD_SEGMENT_ID_PATTERN = "p[A-Z]+|t\\d+r\\d+c\\d+";

const truncate = (text: string, limit: number) =>
  text.length > limit ? text.substring(0, limit - 3) + "..." : text;

/**
 * Enrich citation labels and create bookmarks so they can navigate into the
 * document even after subsequent edits.
 */
export const enrichCitations = async (citations: Citation[], docModel: DocumentModel) => {
  for (const cit of citations) {
    const cellRef = parseTableCellId(cit.segmentId);

    if (cellRef && docModel.tables) {
      // Table-cell citation (e.g. "t0r1c2")
      const tableModel = docModel.tables.find((t) => t.tableIndex === cellRef.tableIndex);
      if (tableModel) {
        const cell = tableModel.cells.find(
          (c) => c.row === cellRef.row && c.column === cellRef.column
        );
        const headerCell = tableModel.cells.find((c) => c.row === 0 && c.column === cellRef.column);
        const colName = headerCell?.text || `Col ${cellRef.column + 1}`;
        const cellText = cell?.text || "";
        cit.label = truncate(`Table ${cellRef.tableIndex + 1}, ${colName}: ${cellText}`, 80);
      }
      const scrollResult = await scrollToTableCell(
        cellRef.tableIndex,
        cellRef.row,
        cellRef.column,
        true
      );
      if (scrollResult.success && scrollResult.bookmarkName) {
        cit.bookmarkName = scrollResult.bookmarkName;
      }
    } else {
      // Paragraph citation (e.g. "pF") -- possibly a table-level segment
      const segment = docModel.segments.find((s) => s.segmentId === cit.segmentId);
      if (segment) {
        if (segment.type === "table" && segment.table && docModel.tables) {
          const tableModel = docModel.tables.find(
            (t) => t.tableIndex === segment.table!.tableIndex
          );
          if (tableModel) {
            const headerPreview = tableModel.cells
              .filter((c) => c.row === 0)
              .sort((a, b) => a.column - b.column)
              .map((c) => c.text)
              .join(" | ");
            cit.label = truncate(`Table ${tableModel.tableIndex + 1}: ${headerPreview}`, 80);
          } else {
            cit.label = `Table ${segment.table.tableIndex + 1}`;
          }
        } else {
          cit.label = truncate(segment.text, 60);
        }
      }
      const scrollResult = await scrollToParagraph(cit.segmentId, undefined, true);
      if (scrollResult.success && scrollResult.bookmarkName) {
        cit.bookmarkName = scrollResult.bookmarkName;
      }
    }
  }
};

/**
 * Navigate to a citation. Prefers the bookmark laid down at enrich time, since the
 * paragraph ID may have shifted under later edits; falls back to the ID itself.
 */
export const reveal = async (target: RevealTarget): Promise<void> => {
  if (target.bookmarkName) {
    await scrollToBookmark(target.bookmarkName);
    return;
  }
  await scrollToParagraph(target.segmentId);
};
